/**
 * useDashboard — central state hook backed by Supabase.
 *
 * Strategy: optimistic local state, fire-and-forget Supabase mutations.
 * - On mount (when a user is signed in): load all entities from Supabase
 *   in parallel and populate React state.
 * - Each mutator updates React state immediately (so the UI is snappy)
 *   AND fires the corresponding Supabase upsert/delete in the background.
 * - IDs are generated client-side with crypto.randomUUID() — same UUID
 *   used in React state AND in Supabase, so no swap-after-insert is needed.
 *
 * The component-facing API is unchanged from the previous localStorage
 * version, so no caller code needs updates.
 */

import { useEffect, useState, useCallback } from "react";
import * as db from "../lib/db";
import { supabase } from "../lib/supabaseClient";
import { defaults as defaultState } from "../lib/storage";

const uid = () => crypto.randomUUID();

export function useDashboard(user) {
  const [state, setState] = useState(defaultState);
  const [loading, setLoading] = useState(true);
  const ownerId = user?.id;

  // ── Initial load ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!ownerId) return;
    let cancelled = false;
    setLoading(true);
    db.loadAll(ownerId).then((loaded) => {
      if (cancelled) return;
      setState((s) => ({
        ...s,
        clients: loaded.clients,
        offers: loaded.offers,
        payments: loaded.payments,
        team: loaded.team,
        vacations: loaded.vacations || [],
        contractorPayments: loaded.contractorPayments || [],
        tools: loaded.tools,
        toolTransactions: loaded.toolTransactions || [],
        oneOffPayments: loaded.oneOffPayments,
        fxRates: loaded.fxRates,
        partnerRules: loaded.partnerRules || [],
        dividends: loaded.dividends || [],
        settings: loaded.settings ? { ...defaultState().settings, ...loaded.settings } : defaultState().settings,
      }));
      setLoading(false);
    }).catch((e) => {
      console.error("Failed to load from Supabase:", e);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [ownerId]);

  // Dark mode side effect
  useEffect(() => {
    if (state.settings.darkMode) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
  }, [state.settings.darkMode]);

  // ── Clients ──────────────────────────────────────────────────────────────
  const addClient = useCallback((data) => {
    const client = {
      id: uid(),
      status: "active",
      churnDate: null,
      notes: "",
      externalId: null,
      setupPrice: 0,
      ...data,
    };
    setState((s) => ({ ...s, clients: [...s.clients, client] }));
    if (ownerId) db.upsertClient(client, ownerId);
    return client;
  }, [ownerId]);

  const updateClient = useCallback((id, patch) => {
    setState((s) => {
      const next = s.clients.map((c) => (c.id === id ? { ...c, ...patch } : c));
      const updated = next.find((c) => c.id === id);
      if (ownerId && updated) db.upsertClient(updated, ownerId);
      return { ...s, clients: next };
    });
  }, [ownerId]);

  const deleteClient = useCallback((id) => {
    setState((s) => ({
      ...s,
      clients: s.clients.filter((c) => c.id !== id),
      payments: s.payments.filter((p) => p.clientId !== id),
    }));
    if (ownerId) db.deleteClient(id);
  }, [ownerId]);

  const deleteClientsBulk = useCallback((ids) => {
    const idSet = new Set(ids);
    setState((s) => ({
      ...s,
      clients: s.clients.filter((c) => !idSet.has(c.id)),
      payments: s.payments.filter((p) => !idSet.has(p.clientId)),
    }));
    if (ownerId) db.deleteClientsBulk(ids);
  }, [ownerId]);

  // Merge multiple clients into one. The "keeper" absorbs the others':
  //   - emails (added to aliasEmails, deduped, primary excluded)
  //   - notes (concatenated)
  //   - manual payments + manual one-offs (reassigned clientId)
  // Other clients are deleted. Stripe charges + GHL invoices auto-resolve
  // to the keeper on next refresh thanks to findClientByEmail honoring
  // aliasEmails — no per-row reassignment needed for those.
  const mergeClients = useCallback((keeperId, absorbIds) => {
    setState((s) => {
      const keeper = s.clients.find((c) => c.id === keeperId);
      if (!keeper) return s;
      const absorbSet = new Set(absorbIds.filter((id) => id !== keeperId));
      const absorbed = s.clients.filter((c) => absorbSet.has(c.id));
      if (absorbed.length === 0) return s;

      // Build the merged alias-email list: existing aliases + absorbed primary + their aliases.
      const lower = (x) => (x || "").trim().toLowerCase();
      const keeperPrimary = lower(keeper.email);
      const aliasSet = new Set(
        (keeper.aliasEmails || []).map(lower).filter((x) => x && x !== keeperPrimary)
      );
      for (const a of absorbed) {
        if (a.email && lower(a.email) !== keeperPrimary) aliasSet.add(lower(a.email));
        for (const ae of a.aliasEmails || []) {
          if (ae && lower(ae) !== keeperPrimary) aliasSet.add(lower(ae));
        }
      }
      const mergedNotes = [keeper.notes, ...absorbed.map((a) => a.notes)]
        .filter((x) => x && String(x).trim())
        .join("\n---\n");
      const updatedKeeper = {
        ...keeper,
        aliasEmails: [...aliasSet],
        notes: mergedNotes || keeper.notes || "",
      };

      // Reassign manual payments + one-off payments owned by absorbed clients.
      const reassignedPayments = s.payments.map((p) =>
        absorbSet.has(p.clientId) ? { ...p, clientId: keeperId } : p
      );
      const reassignedOneOffs = (s.oneOffPayments || []).map((p) =>
        absorbSet.has(p.clientId) ? { ...p, clientId: keeperId } : p
      );

      const nextClients = s.clients
        .filter((c) => !absorbSet.has(c.id))
        .map((c) => (c.id === keeperId ? updatedKeeper : c));

      if (ownerId) {
        db.upsertClient(updatedKeeper, ownerId);
        db.deleteClientsBulk([...absorbSet]);
        // Persist reassigned manual payments.
        reassignedPayments
          .filter((p) => p.clientId === keeperId && absorbed.some((a) => s.payments.find((sp) => sp.id === p.id && sp.clientId === a.id)))
          .forEach((p) => db.upsertPayment?.(p, ownerId));
      }

      return {
        ...s,
        clients: nextClients,
        payments: reassignedPayments,
        oneOffPayments: reassignedOneOffs,
      };
    });
  }, [ownerId]);

  // Status helpers — covers the four-state taxonomy used in the UI.
  const setClientStatus = useCallback((id, status, extra = {}) => {
    const patch = { status, ...extra };
    if (status === "cancelled" && !extra.churnDate) {
      const d = new Date();
      patch.churnDate = d.toISOString().slice(0, 10);
    }
    if (status !== "cancelled") {
      patch.churnDate = null;
    }
    updateClient(id, patch);
  }, [updateClient]);

  const markCancelled = useCallback((id, churnDate) => {
    updateClient(id, { status: "cancelled", churnDate });
  }, [updateClient]);

  const reactivateClient = useCallback((id) => {
    updateClient(id, { status: "active", churnDate: null });
  }, [updateClient]);

  const pauseClient = useCallback((id, kind = "free_pause") => {
    updateClient(id, { status: kind });
  }, [updateClient]);

  // Legacy alias — some components may still call markChurned
  const markChurned = markCancelled;

  const addClientsBulk = useCallback((rows) => {
    const created = rows.map((r) => ({
      id: uid(),
      status: "active",
      churnDate: null,
      notes: "",
      externalId: null,
      ...r,
    }));
    setState((s) => ({ ...s, clients: [...s.clients, ...created] }));
    if (ownerId) created.forEach((c) => db.upsertClient(c, ownerId));
    return created;
  }, [ownerId]);

  // ── Offers ───────────────────────────────────────────────────────────────
  const addOffer = useCallback((data) => {
    const offer = { id: uid(), description: "", department: null, ...data };
    setState((s) => ({ ...s, offers: [...s.offers, offer] }));
    if (ownerId) db.upsertOffer(offer, ownerId);
    return offer;
  }, [ownerId]);

  const updateOffer = useCallback((id, patch) => {
    setState((s) => {
      const next = s.offers.map((o) => (o.id === id ? { ...o, ...patch } : o));
      const updated = next.find((o) => o.id === id);
      if (ownerId && updated) db.upsertOffer(updated, ownerId);
      return { ...s, offers: next };
    });
  }, [ownerId]);

  const deleteOffer = useCallback((id) => {
    // Mirror Supabase's `on delete set null` FK behaviour locally so the UI
    // doesn't suddenly hide payments that pointed at this offer. Also clear
    // the offer tag from one-off transactions (Expenses) and Stripe charges
    // so the "Tagged" filter doesn't show ghost references to a deleted
    // offer.
    setState((s) => {
      const orphanedOneOffs = (s.oneOffPayments || []).filter((p) => p.offerId === id);
      // Persist the cleared offerId for orphaned one-offs so reload doesn't
      // resurrect the dangling reference.
      if (ownerId) {
        orphanedOneOffs.forEach((p) => {
          db.upsertOneOffPayment?.({ ...p, offerId: null }, ownerId);
        });
      }
      return {
        ...s,
        offers: s.offers.filter((o) => o.id !== id),
        payments: s.payments.map((p) => (p.offerId === id ? { ...p, offerId: null } : p)),
        clients: s.clients.map((c) => (c.primaryOfferId === id ? { ...c, primaryOfferId: null } : c)),
        oneOffPayments: (s.oneOffPayments || []).map((p) =>
          p.offerId === id ? { ...p, offerId: null } : p
        ),
      };
    });
    // Clear offer_id on stripe_charges in Supabase. Stripe charges live in
    // their own table (not state.payments) so deleteOffer has to scrub them
    // directly. The next page-load via useStripePayments will see the
    // cleared values and the Untagged metric / filter will reflect reality.
    if (ownerId) {
      supabase
        .from("stripe_charges")
        .update({ offer_id: null })
        .eq("offer_id", id)
        .then((r) => { if (r.error) console.error("deleteOffer stripe_charges scrub:", r.error.message); });
    }
    if (ownerId) db.deleteOffer(id);
  }, [ownerId]);

  const addOffersBulk = useCallback((rows) => {
    const created = rows.map((r) => ({ id: uid(), description: "", ...r }));
    setState((s) => ({ ...s, offers: [...s.offers, ...created] }));
    if (ownerId) created.forEach((o) => db.upsertOffer(o, ownerId));
    return created;
  }, [ownerId]);

  // ── Payments ─────────────────────────────────────────────────────────────
  const addPayment = useCallback((data) => {
    const payment = { id: uid(), splitGroupId: null, note: "", ...data };
    setState((s) => ({ ...s, payments: [...s.payments, payment] }));
    if (ownerId) db.upsertPayment(payment, ownerId);
    return payment;
  }, [ownerId]);

  const addSplitPayments = useCallback((rows) => {
    const splitGroupId = uid();
    const newPayments = rows.map((r) => ({
      id: uid(),
      splitGroupId,
      note: r.note || "",
      ...r,
    }));
    setState((s) => ({ ...s, payments: [...s.payments, ...newPayments] }));
    if (ownerId) db.upsertPaymentsBulk(newPayments, ownerId);
    return newPayments;
  }, [ownerId]);

  const updatePayment = useCallback((id, patch) => {
    setState((s) => {
      const next = s.payments.map((p) => (p.id === id ? { ...p, ...patch } : p));
      const updated = next.find((p) => p.id === id);
      if (ownerId && updated) db.upsertPayment(updated, ownerId);
      return { ...s, payments: next };
    });
  }, [ownerId]);

  const deletePayment = useCallback((id) => {
    setState((s) => ({ ...s, payments: s.payments.filter((p) => p.id !== id) }));
    if (ownerId) db.deletePayment(id);
  }, [ownerId]);

  const deletePaymentsBulk = useCallback((ids) => {
    const idSet = new Set(ids);
    setState((s) => ({ ...s, payments: s.payments.filter((p) => !idSet.has(p.id)) }));
    if (ownerId) db.deletePaymentsBulk(ids);
  }, [ownerId]);

  const markPaymentPaid = useCallback((id, paidDate) => {
    updatePayment(id, { status: "paid", paidDate });
  }, [updatePayment]);

  const addPaymentsBulk = useCallback((rows) => {
    const created = rows.map((r) => ({
      id: uid(),
      splitGroupId: null,
      note: "",
      ...r,
    }));
    setState((s) => ({ ...s, payments: [...s.payments, ...created] }));
    if (ownerId) db.upsertPaymentsBulk(created, ownerId);
    return created;
  }, [ownerId]);

  // ── Team ─────────────────────────────────────────────────────────────────
  const addTeamMember = useCallback((data) => {
    const member = {
      id: uid(),
      name: "", role: "", email: "", phone: null, instapayHandle: null, department: null,
      type: "employee",
      monthlySalary: 0,
      startDate: null, endDate: null, status: "active", notes: "",
      offerAllocations: [],
      salaryByMonth: {}, bonusByMonth: {}, paymentsByMonth: {},
      ...data,
    };
    setState((s) => {
      let team = [...(s.team || []), member];
      // Enforce one-owner invariant: if this new member is flagged owner,
      // clear is_owner on every other row + persist those clears.
      if (member.isOwner) {
        team = team.map((m) => {
          if (m.id === member.id) return m;
          if (m.isOwner) {
            const cleared = { ...m, isOwner: false };
            if (ownerId) db.upsertTeamMember(cleared, ownerId);
            return cleared;
          }
          return m;
        });
      }
      return { ...s, team };
    });
    if (ownerId) db.upsertTeamMember(member, ownerId);
    return member;
  }, [ownerId]);

  const updateTeamMember = useCallback((id, patch) => {
    setState((s) => {
      let next = (s.team || []).map((m) => (m.id === id ? { ...m, ...patch } : m));
      const updated = next.find((m) => m.id === id);
      // Enforce one-owner invariant: if this update sets is_owner=true, clear
      // it on every other row + persist those clears in the background.
      if (updated?.isOwner) {
        next = next.map((m) => {
          if (m.id === id) return m;
          if (m.isOwner) {
            const cleared = { ...m, isOwner: false };
            if (ownerId) db.upsertTeamMember(cleared, ownerId);
            return cleared;
          }
          return m;
        });
      }
      if (ownerId && updated) db.upsertTeamMember(updated, ownerId);
      return { ...s, team: next };
    });
  }, [ownerId]);

  // Edit a single (member, month) cell in the salary, bonus, or
  // contractor-payment matrix. Mirrors updateToolCell. Empty/zero value
  // clears the cell so the salary matrix falls back to monthlySalary.
  const updateTeamCell = useCallback((memberId, monthKey, kind, value) => {
    const field = kind === "salary" ? "salaryByMonth"
                : kind === "payment" ? "paymentsByMonth"
                : "bonusByMonth";
    setState((s) => {
      const next = (s.team || []).map((m) => {
        if (m.id !== memberId) return m;
        const nextMap = { ...(m[field] || {}) };
        const n = Number(value);
        if (value === "" || value == null || Number.isNaN(n)) {
          delete nextMap[monthKey];
        } else {
          nextMap[monthKey] = n;
        }
        return { ...m, [field]: nextMap };
      });
      const updated = next.find((m) => m.id === memberId);
      if (ownerId && updated) db.upsertTeamMember(updated, ownerId);
      return { ...s, team: next };
    });
  }, [ownerId]);

  const deleteTeamMember = useCallback((id) => {
    setState((s) => ({
      ...s,
      team: (s.team || []).filter((m) => m.id !== id),
      // Clean up any vacations tied to this member.
      vacations: (s.vacations || []).filter((v) => v.memberId !== id),
    }));
    if (ownerId) db.deleteTeamMember(id);
  }, [ownerId]);

  // ── Vacations ────────────────────────────────────────────────────────────
  const addVacation = useCallback((data) => {
    const vacation = {
      id: uid(),
      memberId: null, startDate: null, endDate: null, notes: "",
      ...data,
    };
    setState((s) => ({ ...s, vacations: [...(s.vacations || []), vacation] }));
    if (ownerId) db.upsertVacation(vacation, ownerId);
    return vacation;
  }, [ownerId]);

  const updateVacation = useCallback((id, patch) => {
    setState((s) => {
      const next = (s.vacations || []).map((v) => (v.id === id ? { ...v, ...patch } : v));
      const updated = next.find((v) => v.id === id);
      if (ownerId && updated) db.upsertVacation(updated, ownerId);
      return { ...s, vacations: next };
    });
  }, [ownerId]);

  const deleteVacation = useCallback((id) => {
    setState((s) => ({ ...s, vacations: (s.vacations || []).filter((v) => v.id !== id) }));
    if (ownerId) db.deleteVacation(id);
  }, [ownerId]);

  // ── Contractor payments ──────────────────────────────────────────────────
  const addContractorPayment = useCallback((data) => {
    const payment = {
      id: uid(),
      contractorId: null, date: null,
      usdAmount: 0, nativeAmount: null, nativeCurrency: null, fxRate: null,
      notes: "",
      ...data,
    };
    setState((s) => ({ ...s, contractorPayments: [...(s.contractorPayments || []), payment] }));
    if (ownerId) db.upsertContractorPayment(payment, ownerId);
    return payment;
  }, [ownerId]);

  const updateContractorPayment = useCallback((id, patch) => {
    setState((s) => {
      const next = (s.contractorPayments || []).map((p) => (p.id === id ? { ...p, ...patch } : p));
      const updated = next.find((p) => p.id === id);
      if (ownerId && updated) db.upsertContractorPayment(updated, ownerId);
      return { ...s, contractorPayments: next };
    });
  }, [ownerId]);

  const deleteContractorPayment = useCallback((id) => {
    setState((s) => ({ ...s, contractorPayments: (s.contractorPayments || []).filter((p) => p.id !== id) }));
    if (ownerId) db.deleteContractorPayment(id);
  }, [ownerId]);

  // ── Tool transactions (variable tools) ───────────────────────────────────
  const addToolTransaction = useCallback((data) => {
    const tx = {
      id: uid(),
      toolId: null, date: null,
      usdAmount: 0, nativeAmount: null, nativeCurrency: null, fxRate: null,
      notes: "",
      ...data,
    };
    setState((s) => ({ ...s, toolTransactions: [...(s.toolTransactions || []), tx] }));
    if (ownerId) db.upsertToolTransaction(tx, ownerId);
    return tx;
  }, [ownerId]);

  const updateToolTransaction = useCallback((id, patch) => {
    setState((s) => {
      const next = (s.toolTransactions || []).map((p) => (p.id === id ? { ...p, ...patch } : p));
      const updated = next.find((p) => p.id === id);
      if (ownerId && updated) db.upsertToolTransaction(updated, ownerId);
      return { ...s, toolTransactions: next };
    });
  }, [ownerId]);

  const deleteToolTransaction = useCallback((id) => {
    setState((s) => ({ ...s, toolTransactions: (s.toolTransactions || []).filter((p) => p.id !== id) }));
    if (ownerId) db.deleteToolTransaction(id);
  }, [ownerId]);

  // ── Tools ────────────────────────────────────────────────────────────────
  const addTool = useCallback((data) => {
    const tool = {
      id: uid(),
      name: "", category: "", unitLabel: "", status: "active", notes: "",
      unitsByMonth: {}, priceByMonth: {},
      offerAllocations: [],
      ...data,
    };
    setState((s) => ({ ...s, tools: [...(s.tools || []), tool] }));
    if (ownerId) db.upsertTool(tool, ownerId);
    return tool;
  }, [ownerId]);

  const updateTool = useCallback((id, patch) => {
    setState((s) => {
      const next = (s.tools || []).map((t) => (t.id === id ? { ...t, ...patch } : t));
      const updated = next.find((t) => t.id === id);
      if (ownerId && updated) db.upsertTool(updated, ownerId);
      return { ...s, tools: next };
    });
  }, [ownerId]);

  const updateToolCell = useCallback((toolId, monthKey, kind, value) => {
    const field = kind === "units" ? "unitsByMonth" : "priceByMonth";
    setState((s) => {
      const next = (s.tools || []).map((t) => {
        if (t.id !== toolId) return t;
        const nextMap = { ...(t[field] || {}) };
        if (value === "" || value == null || Number.isNaN(Number(value))) {
          delete nextMap[monthKey];
        } else {
          nextMap[monthKey] = Number(value);
        }
        return { ...t, [field]: nextMap };
      });
      const updated = next.find((t) => t.id === toolId);
      if (ownerId && updated) db.upsertTool(updated, ownerId);
      return { ...s, tools: next };
    });
  }, [ownerId]);

  const setToolStatus = useCallback((id, status) => {
    updateTool(id, { status });
  }, [updateTool]);

  const deleteTool = useCallback((id) => {
    setState((s) => ({ ...s, tools: (s.tools || []).filter((t) => t.id !== id) }));
    if (ownerId) db.deleteTool(id);
  }, [ownerId]);

  // ── One-off payments ─────────────────────────────────────────────────────
  const addOneOffPayment = useCallback((data) => {
    const op = { id: uid(), name: "", category: "", date: null, amount: 0, notes: "", offerId: null, costType: "opex", department: null, ...data };
    setState((s) => ({ ...s, oneOffPayments: [...(s.oneOffPayments || []), op] }));
    if (ownerId) db.upsertOneOffPayment(op, ownerId);
    return op;
  }, [ownerId]);

  const updateOneOffPayment = useCallback((id, patch) => {
    setState((s) => {
      const next = (s.oneOffPayments || []).map((p) => (p.id === id ? { ...p, ...patch } : p));
      const updated = next.find((p) => p.id === id);
      if (ownerId && updated) db.upsertOneOffPayment(updated, ownerId);
      return { ...s, oneOffPayments: next };
    });
  }, [ownerId]);

  const deleteOneOffPayment = useCallback((id) => {
    setState((s) => ({ ...s, oneOffPayments: (s.oneOffPayments || []).filter((p) => p.id !== id) }));
    if (ownerId) db.deleteOneOffPayment(id);
  }, [ownerId]);

  // ── Dividends (money paid out to Ahmed / partners) ───────────────────────
  const addDividend = useCallback((data) => {
    const dividend = {
      id: uid(),
      date: new Date().toISOString().slice(0, 10),
      department: null,
      recipient: "",
      amount: 0,
      paymentMethod: null,
      notes: "",
      ...data,
    };
    setState((s) => ({ ...s, dividends: [...(s.dividends || []), dividend] }));
    if (ownerId) db.upsertDividend(dividend, ownerId);
    return dividend;
  }, [ownerId]);

  const updateDividend = useCallback((id, patch) => {
    setState((s) => {
      const next = (s.dividends || []).map((d) => (d.id === id ? { ...d, ...patch } : d));
      const updated = next.find((d) => d.id === id);
      if (ownerId && updated) db.upsertDividend(updated, ownerId);
      return { ...s, dividends: next };
    });
  }, [ownerId]);

  const deleteDividend = useCallback((id) => {
    setState((s) => ({ ...s, dividends: (s.dividends || []).filter((d) => d.id !== id) }));
    if (ownerId) db.deleteDividend(id);
  }, [ownerId]);

  // ── Partner rules (revenue-share / profit-share cuts) ────────────────────
  const addPartnerRule = useCallback((data) => {
    const rule = {
      id: uid(),
      partnerId: null,
      basis: "revenue",          // revenue | net_profit
      scope: "department",        // department | offer | agency
      scopeId: null,
      percent: 0,
      startsOn: null,
      endsOn: null,
      notes: "",
      ...data,
    };
    setState((s) => ({ ...s, partnerRules: [...(s.partnerRules || []), rule] }));
    if (ownerId) db.upsertPartnerRule(rule, ownerId);
    return rule;
  }, [ownerId]);

  const updatePartnerRule = useCallback((id, patch) => {
    setState((s) => {
      const next = (s.partnerRules || []).map((r) => (r.id === id ? { ...r, ...patch } : r));
      const updated = next.find((r) => r.id === id);
      if (ownerId && updated) db.upsertPartnerRule(updated, ownerId);
      return { ...s, partnerRules: next };
    });
  }, [ownerId]);

  const deletePartnerRule = useCallback((id) => {
    setState((s) => ({ ...s, partnerRules: (s.partnerRules || []).filter((r) => r.id !== id) }));
    if (ownerId) db.deletePartnerRule(id);
  }, [ownerId]);

  // ── FX rates ─────────────────────────────────────────────────────────────
  const upsertFxRate = useCallback((month, usdToEgp) => {
    const rate = Number(usdToEgp);
    setState((s) => {
      const filtered = (s.fxRates || []).filter((r) => r.month !== month);
      if (!(rate > 0)) {
        return { ...s, fxRates: filtered };
      }
      const updated = { month, usdToEgp: rate };
      const next = [...filtered, updated].sort((a, b) => a.month.localeCompare(b.month));
      if (ownerId) db.upsertFxRate(updated, ownerId);
      return { ...s, fxRates: next };
    });
  }, [ownerId]);

  const deleteFxRate = useCallback((month) => {
    setState((s) => ({ ...s, fxRates: (s.fxRates || []).filter((r) => r.month !== month) }));
    if (ownerId) db.deleteFxRate(month, ownerId);
  }, [ownerId]);

  // ── Settings ─────────────────────────────────────────────────────────────
  const updateSettings = useCallback((patch) => {
    setState((s) => {
      const nextSettings = { ...s.settings, ...patch };
      if (ownerId) db.upsertSettings(nextSettings, ownerId);
      return { ...s, settings: nextSettings };
    });
  }, [ownerId]);

  // ── Backup (full state to/from JSON file) ────────────────────────────────
  const exportData = useCallback(() => JSON.stringify(state, null, 2), [state]);

  const importData = useCallback((text) => {
    const parsed = JSON.parse(text);
    setState((s) => ({ ...s, ...parsed, settings: { ...s.settings, ...(parsed.settings || {}) } }));
    if (!ownerId) return;
    // Push all imported records to Supabase
    (parsed.clients || []).forEach((c) => db.upsertClient(c, ownerId));
    (parsed.offers || []).forEach((o) => db.upsertOffer(o, ownerId));
    (parsed.payments || []).forEach((p) => db.upsertPayment(p, ownerId));
    (parsed.team || []).forEach((m) => db.upsertTeamMember(m, ownerId));
    (parsed.tools || []).forEach((t) => db.upsertTool(t, ownerId));
    (parsed.oneOffPayments || []).forEach((p) => db.upsertOneOffPayment(p, ownerId));
    (parsed.partnerRules || []).forEach((r) => db.upsertPartnerRule(r, ownerId));
    (parsed.fxRates || []).forEach((r) => db.upsertFxRate(r, ownerId));
    if (parsed.settings) db.upsertSettings(parsed.settings, ownerId);
  }, [ownerId]);

  const clearAllData = useCallback(() => {
    setState(defaultState());
    // Note: this only clears LOCAL state. Use SQL or the Supabase UI to wipe
    // server data if needed. We don't expose a "clear everything on server"
    // button to avoid accidental data loss.
  }, []);

  // Legacy stub (loading sample data not supported in cloud mode — never wired in UI)
  const loadSampleData = useCallback(() => {}, []);

  // Legacy stub (GHL sync data lives in a different code path now)
  const ghlData = { invoices: [], fetchedAt: null, loading: false, error: null };
  const refreshGhl = useCallback(() => {}, []);

  return {
    state,
    loading,
    // clients
    addClient, updateClient, deleteClient, deleteClientsBulk, mergeClients,
    setClientStatus, markCancelled, markChurned,
    reactivateClient, pauseClient, addClientsBulk,
    // offers
    addOffer, updateOffer, deleteOffer, addOffersBulk,
    // payments
    addPayment, addSplitPayments, updatePayment, deletePayment, deletePaymentsBulk,
    markPaymentPaid, addPaymentsBulk,
    // team
    addTeamMember, updateTeamMember, updateTeamCell, deleteTeamMember,
    // vacations
    addVacation, updateVacation, deleteVacation,
    // contractor payments
    addContractorPayment, updateContractorPayment, deleteContractorPayment,
    // tool transactions (variable tools)
    addToolTransaction, updateToolTransaction, deleteToolTransaction,
    // tools
    addTool, updateTool, updateToolCell, setToolStatus, deleteTool,
    // one-off payments
    addOneOffPayment, updateOneOffPayment, deleteOneOffPayment,
    // dividends (money paid out)
    addDividend, updateDividend, deleteDividend,
    // partner rules (revenue/profit share cuts)
    addPartnerRule, updatePartnerRule, deletePartnerRule,
    // fx
    upsertFxRate, deleteFxRate,
    // settings & bulk
    updateSettings, loadSampleData, clearAllData, exportData, importData,
    // legacy GHL pass-through
    ghlData, refreshGhl,
  };
}
